/**
 * @fileoverview Modal component for creating, configuring and editing FanMessages.
 * Includes interactive visual selector for all 11 formats, reactive character counters
 * with visual warnings (yellow at 80%, red at 100%), typography and color adjustments,
 * drag-and-drop media uploads, and integrated real-time Live Preview.
 * 
 * @module components/Admin/Games/FanMessages/FanMessageModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { BLSaveMessage, BLUploadMedia } from './../../../../utilities/BL/FanMessages';
import { FanMessagePreview, FAN_MESSAGE_FORMATS, resolveMediaUrl } from './FanMessagePreview';

/**
 * Character limit constants.
 */
const LIMITS = {
    TITLE_MAX: 60,
    MAIN_TEXT_MAX: 120,
    SUBTITLE_MAX: 40
};

/**
 * Reusable Drag & Drop Field for media assets within the modal.
 * 
 * @function DropzoneField
 * @param {Object} props - Properties.
 * @param {string} props.label - Label string.
 * @param {string} props.currentValue - Asset URL.
 * @param {string} props.fieldName - Property name.
 * @param {string} props.mediaType - Category for server optimization ('background', 'logo', 'qr').
 * @param {Function} props.onFileSelected - Callback with file and field.
 * @param {boolean} props.isUploading - Upload in progress indicator.
 * @param {string} [props.helpText] - Informational subtitle.
 */
const DropzoneField = ({
    label,
    currentValue,
    fieldName,
    mediaType,
    onFileSelected,
    isUploading = false,
    helpText = ''
}) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            onFileSelected(file, mediaType, fieldName);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onFileSelected(file, mediaType, fieldName);
        }
    };

    return (
        <div className="mb-3">
            {label && <label className="form-label fw-bold text-dark mb-1">{label}</label>}
            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${
                    isDragOver ? 'border-primary bg-primary-subtle shadow-sm' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`modal-upload-${fieldName}`).click()}
                style={{ minHeight: '85px', cursor: 'pointer' }}
            >
                {isUploading ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo y optimizando medio...</span>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-4 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && <small className="text-secondary d-block mt-1">{helpText}</small>}
                    </>
                )}
                <input
                    id={`modal-upload-${fieldName}`}
                    type="file"
                    className="d-none"
                    accept="image/*,video/mp4,video/webm"
                    onChange={handleFileChange}
                />
            </div>
            {currentValue && (
                <div className="mt-2 d-flex align-items-center justify-content-between bg-white p-2 rounded-3 border shadow-sm">
                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                        {currentValue.endsWith('.mp4') || currentValue.endsWith('.webm') ? (
                            <i className="bi bi-camera-video-fill text-danger fs-5"></i>
                        ) : (
                            <img
                                src={resolveMediaUrl(currentValue)}
                                alt="Vista previa"
                                style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        )}
                        <span className="small text-truncate text-muted" style={{ maxWidth: '280px' }}>
                            {currentValue}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => {
                            e.stopPropagation();
                            onFileSelected(null, mediaType, fieldName);
                        }}
                        title="Eliminar archivo"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * Character counter badge helper.
 * 
 * @function CharacterCounter
 * @param {Object} props - Properties.
 * @param {number} props.current - Current character count.
 * @param {number} props.max - Maximum permitted characters.
 */
const CharacterCounter = ({ current, max }) => {
    const ratio = current / max;
    let badgeClass = 'bg-secondary';
    if (ratio >= 1.0) {
        badgeClass = 'bg-danger animate-pulse';
    } else if (ratio >= 0.8) {
        badgeClass = 'bg-warning text-dark';
    }

    return (
        <span className={`badge ${badgeClass} ms-2`}>
            {current}/{max}
        </span>
    );
};

/**
 * FanMessageModal Component.
 * 
 * @function FanMessageModal
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Modal open status.
 * @param {Function} props.onCloseModal - Dismiss callback.
 * @param {Object} [props.initialMessageData=null] - Pre-loaded message for editing.
 * @param {Function} props.onMessageSavedSuccessfully - Saved callback with new message record.
 * @returns {React.ReactElement|null} Modal markup.
 */
export const FanMessageModal = ({
    isOpen,
    onCloseModal,
    initialMessageData = null,
    onMessageSavedSuccessfully
}) => {
    const { showMessage } = useFloatMessage();
    const [activeTabKey, setActiveTabKey] = useState('format_text');
    const [isSaving, setIsSaving] = useState(false);
    const [uploadingFieldKey, setUploadingFieldKey] = useState('');

    const defaultFormState = {
        id: 0,
        title: '',
        formatId: 1,
        mainText: '',
        subtitleText: '',
        backgroundType: 'transparent',
        backgroundUrl: '',
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        leftImageUrl: '',
        leftQrUrl: '',
        rightImageUrl: '',
        rightQrUrl: '',
        textColor: '#FFFFFF',
        subtitleColor: '#FCE68A',
        fontFamily: 'Montserrat',
        fontSizePx: 48,
        offsetY: 0
    };

    const [formState, setFormState] = useState(defaultFormState);

    useEffect(() => {
        if (initialMessageData && initialMessageData.id > 0) {
            setFormState({
                ...defaultFormState,
                ...initialMessageData
            });
        } else {
            setFormState(defaultFormState);
        }
        setActiveTabKey('format_text');
    }, [initialMessageData, isOpen]);

    if (!isOpen) return null;

    const handleFieldChange = (fieldName, fieldValue) => {
        setFormState((prev) => ({
            ...prev,
            [fieldName]: fieldValue
        }));
    };

    const handleFileUpload = async (fileObject, mediaType, fieldName) => {
        if (!fileObject) {
            handleFieldChange(fieldName, '');
            return;
        }

        setUploadingFieldKey(fieldName);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo y procesando medio...' });

        try {
            const uploadResponse = await BLUploadMedia(fileObject, mediaType);
            if (uploadResponse.success && uploadResponse.url) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo cargado con éxito.' });
                handleFieldChange(fieldName, uploadResponse.url);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse.message || 'Error al subir archivo multimedia.'
                });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al subir archivo.' });
        } finally {
            setUploadingFieldKey('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formState.title || formState.title.trim() === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Por favor asigna un título descriptivo al mensaje.' });
            setActiveTabKey('format_text');
            return;
        }

        if (formState.title.trim().length > LIMITS.TITLE_MAX) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: `El título no debe superar los ${LIMITS.TITLE_MAX} caracteres.` });
            setActiveTabKey('format_text');
            return;
        }

        if (!formState.mainText || formState.mainText.trim() === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Por favor ingresa el texto principal a proyectar.' });
            setActiveTabKey('format_text');
            return;
        }

        if (formState.mainText.trim().length > LIMITS.MAIN_TEXT_MAX) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: `El texto principal no debe superar los ${LIMITS.MAIN_TEXT_MAX} caracteres.` });
            setActiveTabKey('format_text');
            return;
        }

        if (formState.subtitleText && formState.subtitleText.trim().length > LIMITS.SUBTITLE_MAX) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: `El subtítulo no debe superar los ${LIMITS.SUBTITLE_MAX} caracteres.` });
            setActiveTabKey('format_text');
            return;
        }

        setIsSaving(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando mensaje a la afición...' });

        try {
            const saveResponse = await BLSaveMessage(formState);
            if (saveResponse.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Mensaje "${formState.title}" guardado correctamente.`
                });
                if (onMessageSavedSuccessfully) {
                    onMessageSavedSuccessfully(saveResponse.data);
                }
                onCloseModal();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResponse.message || 'Error al guardar el mensaje.'
                });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al persistir el mensaje.' });
        } finally {
            setIsSaving(false);
        }
    };

    const mainTextRatio = formState.mainText.length / LIMITS.MAIN_TEXT_MAX;

    return (
        <>
            <div className="cModal__Background" onClick={onCloseModal}></div>
            <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '1100px', width: '96vw' }}>
                <div
                    className="cModal__Container__Content bg-white rounded-4 shadow-lg p-0 overflow-hidden text-dark position-relative"
                    style={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* MODAL HEADER */}
                    <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-dark text-white border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-chat-square-quote-fill text-warning fs-4"></i>
                            <h5 className="m-0 fw-bold">
                                {formState.id > 0 ? `Editar Mensaje: ${formState.title}` : 'Crear Nuevo Mensaje a la Afición'}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle"
                            onClick={onCloseModal}
                            title="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* LIVE PREVIEW BANNER CONTAINER (ALWAYS VISIBLE IN TOP) */}
                    <div className="p-3 bg-dark border-bottom border-secondary">
                        <FanMessagePreview
                            message={formState}
                            mode="modal"
                            showContainerFrame={false}
                        />
                    </div>

                    {/* TABS NAVIGATION */}
                    <div className="bg-light border-bottom px-3 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-x-auto">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'format_text' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('format_text')}
                                >
                                    <i className="bi bi-layout-text-window me-1"></i> 1. Formato & Textos
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'styles' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('styles')}
                                >
                                    <i className="bi bi-palette me-1"></i> 2. Estilo & Tipografía
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'background' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('background')}
                                >
                                    <i className="bi bi-image me-1"></i> 3. Fondo de Cintillo
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'sides' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('sides')}
                                >
                                    <i className="bi bi-qr-code-scan me-1"></i> 4. Laterales (Logos & QRs)
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* TAB BODY (SCROLLABLE FORM) */}
                    <form onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden m-0 p-0">
                        <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(92vh - 270px)' }}>
                            {/* ============================================================ */}
                            {/* TAB 1: FORMATO Y TEXTOS                                      */}
                            {/* ============================================================ */}
                            {activeTabKey === 'format_text' && (
                                <div className="row g-3">
                                    {/* Selector de los 11 Formatos Visuales */}
                                    <div className="col-12">
                                        <label className="form-label fw-bold d-flex align-items-center justify-content-between">
                                            <span>
                                                <i className="bi bi-grid-3x3-gap-fill text-primary me-1"></i> Selecciona el Formato Visual (1 al 11):
                                            </span>
                                            <span className="badge bg-primary">
                                                Formato Actual: #{formState.formatId}
                                            </span>
                                        </label>
                                        <div
                                            className="d-grid gap-2 mb-3"
                                            style={{
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                                maxHeight: '220px',
                                                overflowY: 'auto',
                                                padding: '4px'
                                            }}
                                        >
                                            {FAN_MESSAGE_FORMATS.map((fmt) => {
                                                const isSelected = formState.formatId === fmt.id;
                                                return (
                                                    <div
                                                        key={fmt.id}
                                                        onClick={() => handleFieldChange('formatId', fmt.id)}
                                                        className={`p-2 rounded-3 border text-start cursor-pointer transition ${
                                                            isSelected
                                                                ? 'border-primary bg-primary text-white shadow-sm'
                                                                : 'border-secondary-subtle bg-light text-dark hover-shadow'
                                                        }`}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <i className={`bi ${fmt.icon} fs-5 ${isSelected ? 'text-warning' : 'text-primary'}`}></i>
                                                            <strong className="small text-truncate">{fmt.label}</strong>
                                                        </div>
                                                        <p className={`small m-0 text-truncate ${isSelected ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '11px' }}>
                                                            {fmt.description}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Título interno descriptivo */}
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold d-flex align-items-center justify-content-between">
                                            <span>Título interno (Para identificación en panel) *</span>
                                            <CharacterCounter current={formState.title.length} max={LIMITS.TITLE_MAX} />
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${formState.title.length > LIMITS.TITLE_MAX ? 'is-invalid' : ''}`}
                                            placeholder="ej. Mención Cumpleaños Socio #104, QR Descarga App ADSL..."
                                            value={formState.title}
                                            maxLength={LIMITS.TITLE_MAX}
                                            onChange={(e) => handleFieldChange('title', e.target.value)}
                                            required
                                        />
                                    </div>

                                    {/* Subtítulo opcional */}
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold d-flex align-items-center justify-content-between">
                                            <span>Subtítulo o Remitente (Opcional)</span>
                                            <CharacterCounter current={(formState.subtitleText || '').length} max={LIMITS.SUBTITLE_MAX} />
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${(formState.subtitleText || '').length > LIMITS.SUBTITLE_MAX ? 'is-invalid' : ''}`}
                                            placeholder="ej. DE: FAMILIA GONZÁLEZ / ¡VAMOS ATLETI!"
                                            value={formState.subtitleText || ''}
                                            maxLength={LIMITS.SUBTITLE_MAX}
                                            onChange={(e) => handleFieldChange('subtitleText', e.target.value)}
                                        />
                                    </div>

                                    {/* Texto principal a proyectar */}
                                    <div className="col-12">
                                        <label className="form-label fw-bold d-flex align-items-center justify-content-between">
                                            <span>Texto principal a proyectar en pantalla gigante *</span>
                                            <CharacterCounter current={formState.mainText.length} max={LIMITS.MAIN_TEXT_MAX} />
                                        </label>
                                        <textarea
                                            rows={3}
                                            className={`form-control fw-bold ${
                                                mainTextRatio >= 1.0
                                                    ? 'border-danger is-invalid'
                                                    : mainTextRatio >= 0.8
                                                    ? 'border-warning'
                                                    : ''
                                            }`}
                                            placeholder="Escribe aquí el mensaje de alto impacto que se proyectará..."
                                            value={formState.mainText}
                                            maxLength={LIMITS.MAIN_TEXT_MAX}
                                            onChange={(e) => handleFieldChange('mainText', e.target.value)}
                                            required
                                        />
                                        <small className="text-muted d-block mt-1">
                                            Límite sugerido para legibilidad en estadio: 120 caracteres. Se auto-ajustará dinámicamente según la longitud.
                                        </small>
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 2: ESTILO Y TIPOGRAFÍA                                   */}
                            {/* ============================================================ */}
                            {activeTabKey === 'styles' && (
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Familia Tipográfica</label>
                                        <select
                                            className="form-select fw-bold"
                                            value={formState.fontFamily}
                                            onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                                        >
                                            <option value="Montserrat">Montserrat (Moderno & Oficial)</option>
                                            <option value="Bangers">Bangers (Dinámico & Comic)</option>
                                            <option value="Oswald">Oswald (Alto Impacto / Gradas)</option>
                                            <option value="Arial Black">Arial Black / Impact (Robusto)</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold d-flex justify-content-between">
                                            <span>Tamaño de Fuente Principal:</span>
                                            <span className="badge bg-dark">{formState.fontSizePx}px</span>
                                        </label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min={24}
                                            max={72}
                                            step={2}
                                            value={formState.fontSizePx}
                                            onChange={(e) => handleFieldChange('fontSizePx', Number(e.target.value))}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Texto Principal</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={formState.textColor || '#FFFFFF'}
                                                onChange={(e) => handleFieldChange('textColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={formState.textColor || '#FFFFFF'}
                                                onChange={(e) => handleFieldChange('textColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Subtítulo</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={formState.subtitleColor || '#FCE68A'}
                                                onChange={(e) => handleFieldChange('subtitleColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={formState.subtitleColor || '#FCE68A'}
                                                onChange={(e) => handleFieldChange('subtitleColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label fw-bold d-flex justify-content-between">
                                            <span>Desplazamiento Vertical (Offset Y):</span>
                                            <span className="badge bg-secondary">{formState.offsetY}px</span>
                                        </label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min={-80}
                                            max={80}
                                            step={5}
                                            value={formState.offsetY}
                                            onChange={(e) => handleFieldChange('offsetY', Number(e.target.value))}
                                        />
                                        <small className="text-muted">Ajuste fino en píxeles para alinear con cintillos y gráficos de transmisión.</small>
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 3: FONDO DEL CINTILLO                                    */}
                            {/* ============================================================ */}
                            {activeTabKey === 'background' && (
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipo de Fondo</label>
                                        <select
                                            className="form-select fw-bold"
                                            value={formState.backgroundType}
                                            onChange={(e) => handleFieldChange('backgroundType', e.target.value)}
                                        >
                                            <option value="transparent">Cintillo Translúcido (Default)</option>
                                            <option value="color">Color Sólido / HEX</option>
                                            <option value="image">Imagen de Fondo (PNG / JPG / WebP)</option>
                                            <option value="video">Video Loop (MP4 / WebM)</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Fondo</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={formState.backgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('backgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control font-monospace"
                                                value={formState.backgroundColor || 'rgba(15, 23, 42, 0.95)'}
                                                onChange={(e) => handleFieldChange('backgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {(formState.backgroundType === 'image' || formState.backgroundType === 'video') && (
                                        <div className="col-12">
                                            <DropzoneField
                                                label="Recurso de Fondo (1920x300 px recomendado)"
                                                currentValue={formState.backgroundUrl}
                                                fieldName="backgroundUrl"
                                                mediaType="background"
                                                onFileSelected={handleFileUpload}
                                                isUploading={uploadingFieldKey === 'backgroundUrl'}
                                                helpText="Se optimizará automáticamente con SkiaSharp para transmisión sin pérdidas."
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 4: LATERALES (LOGOS Y QRS)                               */}
                            {/* ============================================================ */}
                            {activeTabKey === 'sides' && (
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="p-3 bg-light rounded-4 border">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="bi bi-arrow-left-square-fill me-1"></i> Elemento Lateral Izquierdo
                                            </h6>
                                            <DropzoneField
                                                label="Imagen / Logo Izquierdo (400x400 px)"
                                                currentValue={formState.leftImageUrl}
                                                fieldName="leftImageUrl"
                                                mediaType="logo"
                                                onFileSelected={handleFileUpload}
                                                isUploading={uploadingFieldKey === 'leftImageUrl'}
                                                helpText="Logo del ADSL, patrocinador o avatar."
                                            />
                                            <label className="form-label fw-bold mt-2">O URL para Código QR Izquierdo:</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                placeholder="https://adsl.com.mx/dinamica"
                                                value={formState.leftQrUrl || ''}
                                                onChange={(e) => handleFieldChange('leftQrUrl', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="p-3 bg-light rounded-4 border">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="bi bi-arrow-right-square-fill me-1"></i> Elemento Lateral Derecho
                                            </h6>
                                            <DropzoneField
                                                label="Imagen / Logo Derecho (400x400 px)"
                                                currentValue={formState.rightImageUrl}
                                                fieldName="rightImageUrl"
                                                mediaType="logo"
                                                onFileSelected={handleFileUpload}
                                                isUploading={uploadingFieldKey === 'rightImageUrl'}
                                                helpText="Logo de patrocinador o marca."
                                            />
                                            <label className="form-label fw-bold mt-2">O URL para Código QR Derecho:</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                placeholder="https://adsl.com.mx/registro"
                                                value={formState.rightQrUrl || ''}
                                                onChange={(e) => handleFieldChange('rightQrUrl', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* MODAL FOOTER */}
                        <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-light border-top">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={onCloseModal}
                                disabled={isSaving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-success fw-bold px-4 shadow-sm text-white d-flex align-items-center gap-2"
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                        <span>Guardando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle fs-5"></i>
                                        <span>{formState.id > 0 ? 'Actualizar Mensaje' : 'Guardar Mensaje'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};
